'use client';
import { useState, useEffect, lazy, Suspense } from "react";
import { findWonderNetworkLocation } from "@/lib/data/wondernetwork-locations";
import { ExternalLink } from "lucide-react";
import Input from "@/components/ui/Input";
import { decodeCurrentURL, updateURL } from "@/lib/tools/url-state";
import ShareableLinkButton from "@/components/ShareableLinkButton";

const TOOL_SLUG = "network-latency-calculator";

// URL state schema for deep linking — input fields round-trip through the URL
const URL_SCHEMA = {
  tab: 'string',
  // Latency tab
  origin: 'string',
  dest: 'string',
  medium: 'string',
  mult: 'string',
  // Ping tab
  pingRtt: 'string',
  pingMedium: 'string',
  pingMult: 'string',
  pingCity: 'string',
  // Data transfer tab
  fileSize: 'string',
  fileSizeUnit: 'string',
  bw: 'string',
  bwUnit: 'string',
  transferLatency: 'string',
  mtu: 'string',
  overhead: 'string',
} as const;

const URL_DEFAULTS = {
  tab: 'latency',
  origin: '',
  dest: '',
  medium: 'fiber',
  mult: '2',
  pingRtt: '',
  pingMedium: 'fiber',
  pingMult: '2',
  pingCity: '',
  fileSize: '',
  fileSizeUnit: 'MB',
  bw: '',
  bwUnit: 'Mbps',
  transferLatency: '',
  mtu: '1500',
  overhead: 'none',
};

// Lazy load map components - only loaded after client-side mount
type MapViewProps = {
  coordinates: {
    origin: { lat: number; lon: number };
    destination: { lat: number; lon: number };
  };
};
type PingMapViewProps = {
  cityCoords: { lat: number; lon: number; radiusKm: number };
};

// Client-side only lazy imports (avoid next/dynamic for Cloudflare Pages compatibility)
const LazyMapView = lazy(() => import("./map-view"));
const LazyPingMapView = lazy(() => import("./ping-map-view"));

// Wrapper component that only renders on client
function ClientOnly({ children, fallback }: { children: React.ReactNode; fallback?: React.ReactNode }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  if (!mounted) return fallback || null;
  return <>{children}</>;
}

// Map wrapper that handles lazy loading with Suspense
function MapView(props: MapViewProps) {
  return (
    <ClientOnly fallback={<MapPlaceholder />}>
      <Suspense fallback={<MapPlaceholder />}>
        <LazyMapView {...props} />
      </Suspense>
    </ClientOnly>
  );
}

function PingMapView(props: PingMapViewProps) {
  return (
    <ClientOnly fallback={<MapPlaceholder />}>
      <Suspense fallback={<MapPlaceholder />}>
        <LazyPingMapView {...props} />
      </Suspense>
    </ClientOnly>
  );
}

function MapPlaceholder() {
  return (
    <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded">
      <span className="text-gray-500">Loading map...</span>
    </div>
  );
}

// Medium types with their propagation speeds
const MEDIUM_TYPES = {
  fiber: { name: "Fiber Optic", speed: 200000, description: "~5ms per 1000km" },
  copper: { name: "Copper Cable", speed: 200000, description: "~5ms per 1000km" },
  wireless: { name: "Wireless 5G", speed: 200000, description: "~5ms per 1000km + processing" },
  satellite: { name: "Satellite (GEO)", speed: 300000, description: "~119ms to orbit" },
} as const;
type MediumKey = keyof typeof MEDIUM_TYPES;

type Coordinates = { lat: number; lon: number };
type LocationCoordinates = { origin: Coordinates; destination: Coordinates };
type WonderNetworkLocation = ReturnType<typeof findWonderNetworkLocation>;
type WonderNetworkLink = WonderNetworkLocation | null;
type DistanceResult = { km: string; miles: string };
type PingDistance = {
  km: string;
  miles: string;
  theoreticalKm: string;
  theoreticalMiles: string;
} | null;
type TransferResult = {
  transferTime: string;
  totalTime: string;
  transferTimeSeconds: number;
  totalTimeSeconds: number;
  latencyImpact: string;
  throughput: string;
  formattedTime: string; // HH:MM:SS format
  calculationBreakdown: {
    fileSizeBits: number;
    effectiveBandwidthBps: number;
    protocolOverheadPercent: number;
    rawTransferSeconds: number;
  };
} | null;

export default function NetworkLatencyCalculatorTool() {
  // Load initial state from URL for deep linking
  const initialState = decodeCurrentURL({
    schema: URL_SCHEMA,
    defaults: URL_DEFAULTS,
  });

  const [activeTab, setActiveTab] = useState((initialState.tab as string) || "latency");
  const [origin, setOrigin] = useState((initialState.origin as string) || "");
  const [destination, setDestination] = useState((initialState.dest as string) || "");
  const [distance, setDistance] = useState<DistanceResult>({ km: "", miles: "" });
  const [oneWayLatency, setOneWayLatency] = useState<string>("0");
  const [roundTripLatency, setRoundTripLatency] = useState<string>("0");
  const [realWorldLatency, setRealWorldLatency] = useState<string>("0");
  const [error, setError] = useState<string | null>(null);
  const [coordinates, setCoordinates] = useState<LocationCoordinates | null>(null);
  const [mediumType, setMediumType] = useState<MediumKey>(
    (initialState.medium as MediumKey) || "fiber"
  );
  const [multiplier, setMultiplier] = useState((initialState.mult as string) || "2");
  const [wondernetworkLinks, setWondernetworkLinks] = useState<{ origin: WonderNetworkLink; destination: WonderNetworkLink }>({
    origin: null,
    destination: null,
  });
  const [isCalculating, setIsCalculating] = useState(false);

  // Ping calculator state
  const [pingRTT, setPingRTT] = useState((initialState.pingRtt as string) || "");
  const [estimatedDistance, setEstimatedDistance] = useState<PingDistance>(null);
  const [hasPingEstimate, setHasPingEstimate] = useState(false);
  const [pingError, setPingError] = useState<string | null>(null);
  const [pingMediumType, setPingMediumType] = useState<MediumKey>(
    (initialState.pingMedium as MediumKey) || "fiber"
  );
  const [pingMultiplier, setPingMultiplier] = useState((initialState.pingMult as string) || "2");
  const [pingCity, setPingCity] = useState((initialState.pingCity as string) || "");
  const [pingCityCoords, setPingCityCoords] = useState<{ lat: number; lon: number; radiusKm: number } | null>(null);
  const [showPingMap, setShowPingMap] = useState(false);
  const [pingCityWonderNetwork, setPingCityWonderNetwork] = useState<WonderNetworkLink>(null);
  const [isPingCalculating, setIsPingCalculating] = useState(false);

  // Data Transfer calculator state
  const [fileSize, setFileSize] = useState((initialState.fileSize as string) || "");
  const [fileSizeUnit, setFileSizeUnit] = useState((initialState.fileSizeUnit as string) || "MB");
  const [bandwidth, setBandwidth] = useState((initialState.bw as string) || "");
  const [bandwidthUnit, setBandwidthUnit] = useState((initialState.bwUnit as string) || "Mbps");
  const [transferLatency, setTransferLatency] = useState((initialState.transferLatency as string) || "");
  const [mtu, setMtu] = useState((initialState.mtu as string) || "1500"); // Standard Ethernet MTU in bytes
  const [protocolOverhead, setProtocolOverhead] = useState((initialState.overhead as string) || "none"); // none, tcp, http
  const [transferResult, setTransferResult] = useState<TransferResult>(null);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [isTransferCalculating, setIsTransferCalculating] = useState(false);

  // Sync input fields to URL (debounced) so the calculator is deep-linkable
  useEffect(() => {
    updateURL(
      {
        tab: activeTab,
        origin,
        dest: destination,
        medium: mediumType,
        mult: multiplier,
        pingRtt: pingRTT,
        pingMedium: pingMediumType,
        pingMult: pingMultiplier,
        pingCity,
        fileSize,
        fileSizeUnit,
        bw: bandwidth,
        bwUnit: bandwidthUnit,
        transferLatency,
        mtu,
        overhead: protocolOverhead,
      },
      { debounce: 300 }
    );
  }, [
    activeTab,
    origin,
    destination,
    mediumType,
    multiplier,
    pingRTT,
    pingMediumType,
    pingMultiplier,
    pingCity,
    fileSize,
    fileSizeUnit,
    bandwidth,
    bandwidthUnit,
    transferLatency,
    mtu,
    protocolOverhead,
  ]);


  // File size presets
  const FILE_SIZE_PRESETS = [
    { label: "1 MB", value: "1", unit: "MB" },
    { label: "100 MB", value: "100", unit: "MB" },
    { label: "1 GB", value: "1", unit: "GB" },
    { label: "4.7 GB (DVD)", value: "4.7", unit: "GB" },
    { label: "25 GB (Blu-ray)", value: "25", unit: "GB" },
    { label: "50 GB (Blu-ray DL)", value: "50", unit: "GB" },
  ];

  // Bandwidth presets
  const BANDWIDTH_PRESETS = [
    { label: "1 Mbps", value: "1", unit: "Mbps" },
    { label: "10 Mbps", value: "10", unit: "Mbps" },
    { label: "100 Mbps", value: "100", unit: "Mbps" },
    { label: "1 Gbps", value: "1", unit: "Gbps" },
    { label: "10 Gbps", value: "10", unit: "Gbps" },
  ];

  // Protocol overhead percentages
  const PROTOCOL_OVERHEADS: Record<string, { name: string; overhead: number; description: string }> = {
    none: { name: "None", overhead: 0, description: "Raw bandwidth (theoretical)" },
    tcp: { name: "TCP (~3-5%)", overhead: 0.04, description: "TCP/IP headers and acknowledgments" },
    http: { name: "HTTP (~5-7%)", overhead: 0.06, description: "TCP + HTTP headers" },
  };

  function getCoordinates(
    address: string,
    callback: (error: Error | null, coords: Coordinates | null) => void,
  ) {
    // Geocode through our own edge proxy (/api/geocode) instead of calling
    // Nominatim directly from the browser. Nominatim throttles anonymous
    // browser traffic (its policy requires identified clients at ≤1 req/s),
    // which made calculations silently fail for many users. The proxy
    // identifies itself properly and edge-caches each location for 30 days.
    const url = `/api/geocode?q=${encodeURIComponent(address)}`;

    fetch(url)
      .then(async (response) => {
        const data = (await response.json()) as { lat?: number; lon?: number; error?: string };
        if (response.ok && typeof data.lat === 'number' && typeof data.lon === 'number') {
          callback(null, { lat: data.lat, lon: data.lon });
        } else {
          callback(new Error(data.error || `"${address}" could not be found. Try "City, Country" format.`), null);
        }
      })
      .catch(() => callback(new Error('Network error while looking up the location. Please check your connection and try again.'), null));
  }

  const calculateLatency = () => {
    if (!origin || !destination) {
      setError("Both origin and destination must be provided.");
      return;
    }
    setError(null);
    setIsCalculating(true);

    getCoordinates(origin, (originError, originCoords) => {
      if (originError) {
        setError(`Origin: ${originError.message}`);
        setIsCalculating(false);
        return;
      }

      if (!originCoords) {
        setError(`Origin "${origin}" could not be found. Try "City, Country" format.`);
        setIsCalculating(false);
        return;
      }

      getCoordinates(destination, (destinationError, destinationCoords) => {
        if (destinationError) {
          setError(`Destination: ${destinationError.message}`);
          setIsCalculating(false);
          return;
        }

        if (!destinationCoords) {
          setError(`Destination "${destination}" could not be found. Try "City, Country" format.`);
          setIsCalculating(false);
          return;
        }

        // Haversine formula calculation
        const R = 6371; // Radius of the Earth in km
        const φ1 = (originCoords.lat * Math.PI) / 180;
        const φ2 = (destinationCoords.lat * Math.PI) / 180;
        const Δφ = ((destinationCoords.lat - originCoords.lat) * Math.PI) / 180;
        const Δλ = ((destinationCoords.lon - originCoords.lon) * Math.PI) / 180;

        const a =
          Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
          Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distanceKm = R * c;
        const distanceMiles = distanceKm * 0.621371;

        // Latency calculation based on medium type
        const selectedMedium = MEDIUM_TYPES[mediumType];
        const oneWayLatencyMs = (distanceKm / selectedMedium.speed) * 1000;
        const roundTripLatencyMs = oneWayLatencyMs * 2;

        // Real-world latency with multiplier
        const realWorldMs = roundTripLatencyMs * parseFloat(multiplier);

        // Update state with results
        setDistance({
          km: distanceKm.toFixed(2),
          miles: distanceMiles.toFixed(2),
        });
        setOneWayLatency(oneWayLatencyMs.toFixed(2));
        setRoundTripLatency(roundTripLatencyMs.toFixed(2));
        setRealWorldLatency(realWorldMs.toFixed(2));
        setCoordinates({
          origin: originCoords,
          destination: destinationCoords,
        });

        // Check for WonderNetwork locations
        const originWN = findWonderNetworkLocation(origin);
        const destinationWN = findWonderNetworkLocation(destination);
        setWondernetworkLinks({
          origin: originWN,
          destination: destinationWN
        });
        setIsCalculating(false);
      });
    });
  };

  // Calculate distance from ping RTT
  const calculateDistanceFromPing = () => {
    if (!pingRTT) {
      setPingError("Please enter a ping RTT value.");
      return;
    }
    setPingError(null);
    setIsPingCalculating(true);

    const rtt = parseFloat(pingRTT);
    if (isNaN(rtt) || rtt <= 0) {
      setPingError("Please enter a valid positive number.");
      setIsPingCalculating(false);
      return;
    }

    // Get selected medium's propagation speed
    const selectedMedium = MEDIUM_TYPES[pingMediumType];
    const propagationSpeed = selectedMedium.speed; // km/s
    const realWorldMultiplier = parseFloat(pingMultiplier);

    // RTT is round-trip, so divide by 2 for one-way time
    const oneWayTimeSeconds = (rtt / 1000) / 2;

    // Calculate theoretical distance
    const theoreticalDistanceKm = oneWayTimeSeconds * propagationSpeed;

    // Apply real-world multiplier (divide since distance is less than theoretical suggests)
    const estimatedDistanceKm = theoreticalDistanceKm / realWorldMultiplier;
    const estimatedDistanceMiles = estimatedDistanceKm * 0.621371;

    setEstimatedDistance({
      km: estimatedDistanceKm.toFixed(2),
      miles: estimatedDistanceMiles.toFixed(2),
      theoreticalKm: theoreticalDistanceKm.toFixed(2),
      theoreticalMiles: (theoreticalDistanceKm * 0.621371).toFixed(2)
    });
    setHasPingEstimate(true);
    setIsPingCalculating(false);
  };

  // Auto-recalculate distance when multiplier changes
  useEffect(() => {
    if (!hasPingEstimate) {
      return;
    }

    const rtt = parseFloat(pingRTT);
    if (Number.isNaN(rtt) || rtt <= 0) {
      return;
    }

    const selectedMedium = MEDIUM_TYPES[pingMediumType];
    const propagationSpeed = selectedMedium.speed;
    const realWorldMultiplier = parseFloat(pingMultiplier);
    const oneWayTimeSeconds = (rtt / 1000) / 2;
    const theoreticalDistanceKm = oneWayTimeSeconds * propagationSpeed;
    const estimatedDistanceKm = theoreticalDistanceKm / realWorldMultiplier;
    const estimatedDistanceMiles = estimatedDistanceKm * 0.621371;

    setEstimatedDistance({
      km: estimatedDistanceKm.toFixed(2),
      miles: estimatedDistanceMiles.toFixed(2),
      theoreticalKm: theoreticalDistanceKm.toFixed(2),
      theoreticalMiles: (theoreticalDistanceKm * 0.621371).toFixed(2)
    });
  }, [hasPingEstimate, pingMultiplier, pingMediumType, pingRTT]);

  // Check if city is in WonderNetwork list
  useEffect(() => {
    if (pingCity) {
      const wnLocation = findWonderNetworkLocation(pingCity);
      setPingCityWonderNetwork(wnLocation);
    } else {
      setPingCityWonderNetwork(null);
    }
  }, [pingCity]);

  // Calculate data transfer time
  const calculateTransferTime = () => {
    if (!fileSize || !bandwidth) {
      setTransferError("Please enter both file size and bandwidth.");
      return;
    }
    setTransferError(null);
    setIsTransferCalculating(true);

    const fileSizeNum = parseFloat(fileSize);
    const bandwidthNum = parseFloat(bandwidth);
    const latencyNum = parseFloat(transferLatency) || 0;

    if (isNaN(fileSizeNum) || fileSizeNum <= 0 || isNaN(bandwidthNum) || bandwidthNum <= 0) {
      setTransferError("Please enter valid positive numbers.");
      setIsTransferCalculating(false);
      return;
    }

    // Convert file size to bytes (using decimal for network standards)
    let fileSizeBytes;
    switch (fileSizeUnit) {
      case "KB": fileSizeBytes = fileSizeNum * 1000; break;
      case "MB": fileSizeBytes = fileSizeNum * 1000000; break;
      case "GB": fileSizeBytes = fileSizeNum * 1000000000; break;
      case "TB": fileSizeBytes = fileSizeNum * 1000000000000; break;
      default: fileSizeBytes = fileSizeNum;
    }

    // Convert bandwidth to bits per second
    let bandwidthBps;
    switch (bandwidthUnit) {
      case "Kbps": bandwidthBps = bandwidthNum * 1000; break;
      case "Mbps": bandwidthBps = bandwidthNum * 1000000; break;
      case "Gbps": bandwidthBps = bandwidthNum * 1000000000; break;
      default: bandwidthBps = bandwidthNum;
    }

    // Apply protocol overhead
    const overheadPercent = PROTOCOL_OVERHEADS[protocolOverhead].overhead;
    const effectiveBandwidthAfterOverhead = bandwidthBps * (1 - overheadPercent);

    // Calculate transfer time
    const fileSizeBits = fileSizeBytes * 8;
    const rawTransferTimeSeconds = fileSizeBits / effectiveBandwidthAfterOverhead;

    // Account for latency impact on data transfer:
    // 1. Initial TCP handshake: 1 RTT
    // 2. Data transfer time (affected by bandwidth-delay product for large files)
    // 3. Final acknowledgment: 1 RTT
    // For TCP transfers, latency creates a "pipeline" that must be filled
    const latencySeconds = latencyNum / 1000;

    // TCP connection establishment + termination (2 RTTs total)
    const connectionOverhead = latencySeconds * 2;

    // Calculate packet overhead based on MTU
    const mtuBytes = parseFloat(mtu);
    const ipHeaderBytes = 20; // IPv4 header
    const tcpHeaderBytes = 20; // TCP header (minimum)
    const payloadPerPacket = mtuBytes - ipHeaderBytes - tcpHeaderBytes; // Actual data per packet

    // Calculate number of packets needed
    const numPackets = Math.ceil(fileSizeBytes / payloadPerPacket);

    // Total overhead from headers
    const totalHeaderBytes = numPackets * (ipHeaderBytes + tcpHeaderBytes);
    const totalHeaderBits = totalHeaderBytes * 8;

    // Effective data to transmit (payload + headers)
    const totalBitsToTransmit = fileSizeBits + totalHeaderBits;

    // Now calculate bandwidth-delay product limitation (TCP window size)
    // This is a SEPARATE effect from MTU - both happen!
    const tcpWindowSizeBytes = 65535; // Standard TCP window (64KB)
    const tcpWindowSizeBits = tcpWindowSizeBytes * 8;
    const bandwidthDelayProduct = effectiveBandwidthAfterOverhead * latencySeconds;

    // If bandwidth-delay product exceeds TCP window, throughput is limited
    let effectiveBandwidth = effectiveBandwidthAfterOverhead;
    if (latencyNum > 0 && bandwidthDelayProduct > tcpWindowSizeBits) {
      // Effective throughput limited by: window size / RTT
      effectiveBandwidth = Math.min(effectiveBandwidthAfterOverhead, tcpWindowSizeBits / latencySeconds);
    }

    // Calculate transfer time with effective bandwidth (accounts for both MTU overhead AND window limitation)
    const effectiveTransferTimeSeconds = totalBitsToTransmit / effectiveBandwidth;

    // Add connection overhead
    const totalTimeSeconds = connectionOverhead + effectiveTransferTimeSeconds;

    // Format time in human-readable format
    const formatTime = (seconds: number) => {
      if (seconds < 1) return `${(seconds * 1000).toFixed(2)} ms`;
      if (seconds < 60) return `${seconds.toFixed(2)} seconds`;
      if (seconds < 3600) return `${(seconds / 60).toFixed(2)} minutes`;
      if (seconds < 86400) return `${(seconds / 3600).toFixed(2)} hours`;
      return `${(seconds / 86400).toFixed(2)} days`;
    };

    // Format time as HH:MM:SS
    const formatTimeHMS = (seconds: number) => {
      if (seconds < 1) return `00:00:00 (${(seconds * 1000).toFixed(0)}ms)`;
      const hrs = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = Math.floor(seconds % 60);
      const ms = Math.round((seconds % 1) * 1000);
      if (hrs > 0) {
        return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      }
      if (mins > 0) {
        return `00:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      }
      return `00:00:${secs.toString().padStart(2, '0')}${ms > 0 ? `.${ms}` : ''}`;
    };

    setTransferResult({
      transferTime: formatTime(rawTransferTimeSeconds),
      totalTime: formatTime(totalTimeSeconds),
      transferTimeSeconds: rawTransferTimeSeconds,
      totalTimeSeconds: totalTimeSeconds,
      latencyImpact: latencyNum > 0 ? `${((totalTimeSeconds - rawTransferTimeSeconds) / totalTimeSeconds * 100).toFixed(2)}%` : "0%",
      throughput: `${((fileSizeBytes / effectiveTransferTimeSeconds) / (1000000)).toFixed(2)} MB/s`,
      formattedTime: formatTimeHMS(totalTimeSeconds),
      calculationBreakdown: {
        fileSizeBits: fileSizeBits,
        effectiveBandwidthBps: effectiveBandwidth,
        protocolOverheadPercent: overheadPercent * 100,
        rawTransferSeconds: rawTransferTimeSeconds,
      },
    });
    setIsTransferCalculating(false);
  };

  // Auto-update map when city or distance changes
  useEffect(() => {
    if (!pingCity || !estimatedDistance) {
      setShowPingMap(false);
      setPingCityCoords(null);
      return;
    }

    getCoordinates(pingCity, (error, coords) => {
      if (error || !coords) {
        setShowPingMap(false);
        setPingCityCoords(null);
        return;
      }
      // Use the user's selected distance estimate based on their multiplier
      const radiusKm = parseFloat(estimatedDistance.km);
      setPingCityCoords({
        lat: coords.lat,
        lon: coords.lon,
        radiusKm: radiusKm
      });
      setShowPingMap(true);
    });
  }, [pingCity, estimatedDistance]);

  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-6 w-full max-w-4xl px-2 sm:px-4">
      <div className="flex justify-end">
        <ShareableLinkButton toolSlug={TOOL_SLUG} size="sm" />
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-col sm:flex-row border-b border-gray-200 -mx-2 sm:mx-0">
        <button
          onClick={() => setActiveTab("latency")}
          className={`px-3 sm:px-4 md:px-6 py-3 font-semibold text-xs sm:text-sm transition-colors ${
            activeTab === "latency"
              ? "border-b-2 border-primary text-primary bg-primary/5"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Network Latency
        </button>
        <button
          onClick={() => setActiveTab("ping")}
          className={`px-3 sm:px-4 md:px-6 py-3 font-semibold text-xs sm:text-sm transition-colors ${
            activeTab === "ping"
              ? "border-b-2 border-primary text-primary bg-primary/5"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Ping Distance
        </button>
        <button
          onClick={() => setActiveTab("transfer")}
          className={`px-3 sm:px-4 md:px-6 py-3 font-semibold text-xs sm:text-sm transition-colors ${
            activeTab === "transfer"
              ? "border-b-2 border-primary text-primary bg-primary/5"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Data Transfer
        </button>
      </div>

      {activeTab === "latency" && (
      <>
      <div className="w-full">
        <div className="p-3 sm:p-4 md:p-6">
          <h3 className="text-base sm:text-lg md:text-xl">Network Latency Calculator</h3>
          <p className="text-xs sm:text-sm">Enter two addresses or cities to calculate the distance and latency.</p>
        </div>
        <div className="space-y-3 sm:space-y-4 p-3 sm:p-4 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-1.5 sm:space-y-2">
              <label htmlFor="origin" className="text-xs sm:text-sm">Origin</label>
              <Input
                id="origin"
                placeholder="e.g., San Francisco, CA"
                value={origin}
                onChange={(e) => setOrigin(e.target.value)}
                className="text-xs sm:text-sm h-10"
              />
            </div>
            <div className="space-y-1.5 sm:space-y-2">
              <label htmlFor="destination" className="text-xs sm:text-sm">Destination</label>
              <Input
                id="destination"
                placeholder="e.g., Dallas, TX"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                className="text-xs sm:text-sm h-10"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-1.5 sm:space-y-2">
              <label htmlFor="medium" className="text-xs sm:text-sm">Medium Type</label>
              <select
                id="medium"
                aria-label="Medium Type"
                value={mediumType}
                onChange={(event) => setMediumType(event.target.value as MediumKey)}
                className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-[#2813e8] focus:border-transparent text-xs sm:text-sm h-10 w-full"
              >
                  <option value="fiber" className="text-xs sm:text-sm">Fiber Optic</option>
                  <option value="copper" className="text-xs sm:text-sm">Copper Cable</option>
                  <option value="wireless" className="text-xs sm:text-sm">Wireless 5G</option>
                  <option value="satellite" className="text-xs sm:text-sm">Satellite (GEO)</option>
              </select>
            </div>

            <div className="space-y-1.5 sm:space-y-2">
              <label htmlFor="multiplier" className="text-xs sm:text-sm">Overhead Factor</label>
              <select id="multiplier" aria-label="Overhead Factor" value={multiplier} onChange={(event) => setMultiplier(event.target.value)} className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-[#2813e8] focus:border-transparent text-xs sm:text-sm h-10 w-full">
                  <option value="1.25" className="text-xs sm:text-sm">1.25x - Excellent</option>
                  <option value="1.5" className="text-xs sm:text-sm">1.5x - Very Good</option>
                  <option value="2" className="text-xs sm:text-sm">2.0x - Typical</option>
                  <option value="2.5" className="text-xs sm:text-sm">2.5x - Below Average</option>
                  <option value="3" className="text-xs sm:text-sm">3.0x - Poor</option>
              </select>
            </div>
          </div>

          <button
            onClick={calculateLatency}
            disabled={isCalculating}
            className="w-full bg-[#2813e8] text-white hover:bg-[#2813e8]/90 transition-colors font-semibold rounded-md text-xs sm:text-sm h-10 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCalculating ? (
              <span className="inline-flex items-center justify-center gap-2">
                <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" aria-hidden="true" />
                Looking up locations…
              </span>
            ) : (
              "Calculate"
            )}
          </button>
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2" role="alert">
              <p className="text-red-700 text-xs sm:text-sm">{error}</p>
            </div>
          )}
        </div>

        {/* Map visualization */}
        {coordinates && (
          <div className="p-3 sm:p-4 md:p-6">
            <div className="h-[250px] sm:h-[300px] md:h-[400px] w-full rounded-lg overflow-hidden">
              <MapView coordinates={coordinates} />
            </div>
          </div>
        )}

        {/* Results section */}
        <div className="space-y-3 sm:space-y-4 md:space-y-6 p-3 sm:p-4 md:p-6">
          {distance.km && oneWayLatency && roundTripLatency ? (
            <>
              {/* Key metrics grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 md:gap-4">
                <div className="p-2 sm:p-3 md:p-4 border rounded-lg">
                  <p className="text-xs sm:text-sm text-muted-foreground">Distance</p>
                  <p className="text-lg sm:text-xl md:text-2xl font-bold">{distance.km} km</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">{distance.miles} miles</p>
                </div>
                <div className="p-2 sm:p-3 md:p-4 border rounded-lg">
                  <p className="text-xs sm:text-sm text-muted-foreground">One-Way Latency</p>
                  <p className="text-lg sm:text-xl md:text-2xl font-bold">{oneWayLatency} ms</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">Theoretical</p>
                </div>
                <div className="p-2 sm:p-3 md:p-4 border rounded-lg">
                  <p className="text-xs sm:text-sm text-muted-foreground">Round-Trip Time (RTT)</p>
                  <p className="text-lg sm:text-xl md:text-2xl font-bold">{roundTripLatency} ms</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">Theoretical</p>
                </div>
                <div className="p-2 sm:p-3 md:p-4 border rounded-lg bg-primary/5">
                  <p className="text-xs sm:text-sm text-muted-foreground">Estimated Real-World</p>
                  <p className="text-lg sm:text-xl md:text-2xl font-bold text-primary">{realWorldLatency} ms</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">{multiplier}x multiplier</p>
                </div>
              </div>

              {/* Enhanced Latency Quality Section */}
              <div className="p-3 sm:p-4 md:p-6 border rounded-lg bg-gradient-to-br from-background to-muted/30">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 mb-3 sm:mb-4 md:mb-6">
                  <h3 className="text-sm sm:text-base md:text-lg font-semibold">Latency Quality</h3>
                  <div
                    className={`px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-full font-semibold text-xs sm:text-sm inline-flex items-center gap-1 sm:gap-2 ${
                      parseFloat(realWorldLatency) < 50
                        ? "bg-green-100 text-green-700"
                        : parseFloat(realWorldLatency) < 100
                        ? "bg-yellow-100 text-yellow-700"
                        : parseFloat(realWorldLatency) < 200
                        ? "bg-orange-100 text-orange-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    <span>
                      {parseFloat(realWorldLatency) < 50
                        ? "⚡"
                        : parseFloat(realWorldLatency) < 100
                        ? "✓"
                        : parseFloat(realWorldLatency) < 200
                        ? "⚠"
                        : "✗"}
                    </span>
                    <span>
                      {parseFloat(realWorldLatency) < 50
                        ? "Excellent"
                        : parseFloat(realWorldLatency) < 100
                        ? "Good"
                        : parseFloat(realWorldLatency) < 200
                        ? "Fair"
                        : "Poor"}
                    </span>
                  </div>
                </div>

                {/* Latency value display */}
                <div className="text-center mb-3 sm:mb-4 md:mb-6">
                  <div
                    className={`inline-block text-3xl sm:text-4xl md:text-5xl font-bold ${
                      parseFloat(realWorldLatency) < 50
                        ? "text-green-700"
                        : parseFloat(realWorldLatency) < 100
                        ? "text-yellow-700"
                        : parseFloat(realWorldLatency) < 200
                        ? "text-orange-700"
                        : "text-red-700"
                    }`}
                  >
                    {realWorldLatency}ms
                  </div>
                </div>

                {/* Use case descriptions */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 text-xs">
                  <div
                    className={`p-2 sm:p-2.5 md:p-3 rounded-lg transition-all ${
                      parseFloat(realWorldLatency) < 50
                        ? "bg-green-100 border-2 border-green-600 shadow-sm"
                        : "bg-muted/50 border border-gray-400"
                    }`}
                  >
                    <div
                      className={`font-semibold mb-0.5 sm:mb-1 text-xs sm:text-sm ${
                        parseFloat(realWorldLatency) < 50 ? "text-green-700" : ""
                      }`}
                    >
                      0-50ms
                    </div>
                    <div className="text-muted-foreground text-[10px] sm:text-xs">Gaming, VoIP</div>
                  </div>
                  <div
                    className={`p-2 sm:p-2.5 md:p-3 rounded-lg transition-all ${
                      parseFloat(realWorldLatency) >= 50 && parseFloat(realWorldLatency) < 100
                        ? "bg-yellow-100 border-2 border-yellow-600 shadow-sm"
                        : "bg-muted/50 border border-gray-400"
                    }`}
                  >
                    <div
                      className={`font-semibold mb-0.5 sm:mb-1 text-xs sm:text-sm ${
                        parseFloat(realWorldLatency) >= 50 && parseFloat(realWorldLatency) < 100
                          ? "text-yellow-700"
                          : ""
                      }`}
                    >
                      50-100ms
                    </div>
                    <div className="text-muted-foreground text-[10px] sm:text-xs">Video calls</div>
                  </div>
                  <div
                    className={`p-2 sm:p-2.5 md:p-3 rounded-lg transition-all ${
                      parseFloat(realWorldLatency) >= 100 && parseFloat(realWorldLatency) < 200
                        ? "bg-orange-100 border-2 border-orange-600 shadow-sm"
                        : "bg-muted/50 border border-gray-400"
                    }`}
                  >
                    <div
                      className={`font-semibold mb-0.5 sm:mb-1 text-xs sm:text-sm ${
                        parseFloat(realWorldLatency) >= 100 && parseFloat(realWorldLatency) < 200
                          ? "text-orange-700"
                          : ""
                      }`}
                    >
                      100-200ms
                    </div>
                    <div className="text-muted-foreground text-[10px] sm:text-xs">Email, Files</div>
                  </div>
                  <div
                    className={`p-2 sm:p-2.5 md:p-3 rounded-lg transition-all ${
                      parseFloat(realWorldLatency) >= 200
                        ? "bg-red-100 border-2 border-red-600 shadow-sm"
                        : "bg-muted/50 border border-gray-400"
                    }`}
                  >
                    <div
                      className={`font-semibold mb-0.5 sm:mb-1 text-xs sm:text-sm ${
                        parseFloat(realWorldLatency) >= 200 ? "text-red-700" : ""
                      }`}
                    >
                      200ms+
                    </div>
                    <div className="text-muted-foreground text-[10px] sm:text-xs">Delays</div>
                  </div>
                </div>
              </div>

              {/* Medium type info */}
              <div className="p-3 sm:p-4 bg-muted rounded-lg">
                <h3 className="font-semibold mb-1.5 sm:mb-2 text-xs sm:text-sm md:text-base">Connection Details</h3>
                <p className="text-xs sm:text-sm">
                  <span className="font-semibold">Medium:</span> {MEDIUM_TYPES[mediumType].name} - {MEDIUM_TYPES[mediumType].description}
                </p>
                <p className="text-xs sm:text-sm mt-1">
                  <span className="font-semibold">Calculation:</span> Distance calculated using Haversine formula.
                  Latency based on signal propagation at {MEDIUM_TYPES[mediumType].speed.toLocaleString()} km/s.
                </p>
                <p className="text-xs sm:text-sm mt-2">
                  <span className="font-semibold">Real-World Factor ({multiplier}x):</span> Accounts for routing overhead, network equipment processing delays,
                  packet queuing, and the fact that signals rarely travel in perfectly straight lines.
                </p>

                {/* WonderNetwork Links */}
                {(wondernetworkLinks.origin || wondernetworkLinks.destination) && (
                  <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 p-3 sm:p-4 rounded-lg">
                    <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                      <ExternalLink className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                      <p className="text-xs sm:text-sm md:text-base font-bold text-primary">Real-World Data Available</p>
                    </div>
                    <div className="flex flex-col gap-2 sm:gap-2.5 pl-5 sm:pl-6 md:pl-7">
                      {wondernetworkLinks.origin && (
                        <a
                          href={`https://wondernetwork.com/pings/${encodeURIComponent(wondernetworkLinks.origin.displayName.toLowerCase())}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold text-primary hover:underline hover:text-primary/80 transition-colors"
                        >
                          <ExternalLink className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                          <span className="break-words">View from {wondernetworkLinks.origin.displayName}</span>
                        </a>
                      )}
                      {wondernetworkLinks.destination && (
                        <a
                          href={`https://wondernetwork.com/pings/${encodeURIComponent(wondernetworkLinks.destination.displayName.toLowerCase())}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold text-primary hover:underline hover:text-primary/80 transition-colors"
                        >
                          <ExternalLink className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                          <span className="break-words">View from {wondernetworkLinks.destination.displayName}</span>
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="text-gray-500 text-center py-6 sm:py-8 text-xs sm:text-sm">Enter the addresses and click Calculate to see the results</p>
          )}
        </div>
      </div>

      {/* Comparison Table */}
      <div className="w-full">
        <div className="p-3 sm:p-4 md:p-6">
          <h3 className="text-base sm:text-lg md:text-xl">Latency by Connection Type</h3>
          <p className="text-xs sm:text-sm">Theoretical latency comparison for different network mediums</p>
        </div>
        <div className="p-3 sm:p-4 md:p-6">
          <div className="overflow-x-auto -mx-3 sm:mx-0" tabIndex={0} role="region" aria-label="Latency by connection type comparison table">
            <table className="w-full text-xs sm:text-sm min-w-[500px]">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 sm:py-3 px-2 sm:px-3 md:px-4">Type</th>
                  <th className="text-left py-2 sm:py-3 px-2 sm:px-3 md:px-4">Speed</th>
                  <th className="text-left py-2 sm:py-3 px-2 sm:px-3 md:px-4">Theory</th>
                  <th className="text-left py-2 sm:py-3 px-2 sm:px-3 md:px-4">Real</th>
                  <th className="text-left py-2 sm:py-3 px-2 sm:px-3 md:px-4">Use Case</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4 font-medium">Fiber</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4">~200k km/s</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4">5ms/1000km</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4">10ms/1000km</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4">Long-distance</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4 font-medium">Copper</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4">~200k km/s</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4">5ms/1000km</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4">10ms/1000km</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4">Short-medium</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4 font-medium">5G</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4">~200k km/s</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4">5ms/1000km</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4">10-15ms/1000km</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4">Mobile</td>
                </tr>
                <tr className="hover:bg-muted/50">
                  <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4 font-medium">Satellite</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4">~300k km/s</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4">~119ms</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4">~600ms RTT</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4">Remote</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-[10px] sm:text-xs text-muted-foreground mt-3 sm:mt-4 px-3 sm:px-0">
            <span className="font-semibold">Note:</span> Real-world latency is typically 1.5-3x theoretical minimums due to routing paths (signals don't travel in straight lines),
            router/switch processing delays, packet queuing, and protocol overhead. Data based on actual measurements from global network monitoring.
          </p>
        </div>
      </div>

      </>
      )}

      {activeTab === "ping" && (
        <div className="w-full">
          <div className="p-3 sm:p-4 md:p-6">
            <h3 className="text-base sm:text-lg md:text-xl">Ping Distance Calculator</h3>
            <p className="text-xs sm:text-sm">Enter a ping RTT (Round-Trip Time) to estimate the geographic distance.</p>
          </div>
          <div className="space-y-3 sm:space-y-4 p-3 sm:p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5 sm:space-y-2">
                <label htmlFor="pingRTT" className="text-xs sm:text-sm">Ping RTT (ms)</label>
                <Input
                  id="pingRTT"
                  type="number"
                  placeholder="e.g., 50"
                  value={pingRTT}
                  onChange={(e) => setPingRTT(e.target.value)}
                  className="text-xs sm:text-sm h-10"
                />
              </div>
              <div className="space-y-1.5 sm:space-y-2">
                <label htmlFor="pingCity" className="text-xs sm:text-sm">City (optional)</label>
                <Input
                  id="pingCity"
                  placeholder="e.g., New York"
                  value={pingCity}
                  onChange={(e) => setPingCity(e.target.value)}
                  className="text-xs sm:text-sm h-10"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5 sm:space-y-2">
                <label htmlFor="pingMedium" className="text-xs sm:text-sm">Medium Type</label>
              <select
                id="pingMedium"
                aria-label="Medium Type"
                value={pingMediumType}
                onChange={(event) => setPingMediumType(event.target.value as MediumKey)}
                className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-[#2813e8] focus:border-transparent text-xs sm:text-sm h-10 w-full"
              >
                    <option value="fiber" className="text-xs sm:text-sm">Fiber Optic</option>
                    <option value="copper" className="text-xs sm:text-sm">Copper Cable</option>
                    <option value="wireless" className="text-xs sm:text-sm">Wireless 5G</option>
                    <option value="satellite" className="text-xs sm:text-sm">Satellite (GEO)</option>
                </select>
              </div>

              <div className="space-y-1.5 sm:space-y-2">
                <label htmlFor="pingMultiplier" className="text-xs sm:text-sm">Overhead Factor</label>
              <select id="pingMultiplier" aria-label="Overhead Factor" value={pingMultiplier} onChange={(event) => setPingMultiplier(event.target.value)} className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-[#2813e8] focus:border-transparent text-xs sm:text-sm h-10 w-full">
                    <option value="1.25" className="text-xs sm:text-sm">1.25x - Excellent</option>
                    <option value="1.5" className="text-xs sm:text-sm">1.5x - Very Good</option>
                    <option value="2" className="text-xs sm:text-sm">2.0x - Typical</option>
                    <option value="2.5" className="text-xs sm:text-sm">2.5x - Below Average</option>
                    <option value="3" className="text-xs sm:text-sm">3.0x - Poor</option>
                </select>
              </div>
            </div>

            <button
              onClick={calculateDistanceFromPing}
              disabled={isPingCalculating}
              className="w-full bg-[#2813e8] text-white hover:bg-[#2813e8]/90 transition-colors font-semibold rounded-md text-xs sm:text-sm h-10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPingCalculating ? "Calculating..." : "Calculate Distance"}
            </button>
            {pingError && <p className="text-red-700 text-xs sm:text-sm">{pingError}</p>}

            {/* Map visualization with radius */}
            {showPingMap && pingCityCoords && (
              <div className="h-[250px] sm:h-[300px] md:h-[400px] w-full rounded-lg overflow-hidden border-2 border-primary">
                <PingMapView cityCoords={pingCityCoords} />
              </div>
            )}

            {estimatedDistance && (
              <div className="space-y-3 sm:space-y-4 mt-4 sm:mt-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
                  <div className={`p-2 sm:p-3 md:p-4 rounded-lg ${
                    parseFloat(pingMultiplier) === 3
                      ? "border-4 border-blue-600 bg-red-200"
                      : "border-2 border-red-600 bg-red-200"
                  }`}>
                    <p className="text-[10px] sm:text-xs font-semibold text-red-900 mb-0.5 sm:mb-1">Poor (33%)</p>
                    <p className="text-sm sm:text-base md:text-lg font-bold text-red-900">{(parseFloat(estimatedDistance.theoreticalKm) / 3).toFixed(2)} km</p>
                    <p className="text-[10px] sm:text-xs text-red-900">{(parseFloat(estimatedDistance.theoreticalMiles) / 3).toFixed(2)} mi</p>
                  </div>
                  <div className={`p-2 sm:p-3 md:p-4 rounded-lg ${
                    parseFloat(pingMultiplier) === 2
                      ? "border-4 border-blue-600 bg-yellow-200"
                      : "border-2 border-yellow-600 bg-yellow-200"
                  }`}>
                    <p className="text-[10px] sm:text-xs font-semibold text-yellow-900 mb-0.5 sm:mb-1">Expected (50%)</p>
                    <p className="text-sm sm:text-base md:text-lg font-bold text-yellow-900">{(parseFloat(estimatedDistance.theoreticalKm) / 2).toFixed(2)} km</p>
                    <p className="text-[10px] sm:text-xs text-yellow-900">{(parseFloat(estimatedDistance.theoreticalMiles) / 2).toFixed(2)} mi</p>
                  </div>
                  <div className={`p-2 sm:p-3 md:p-4 rounded-lg ${
                    parseFloat(pingMultiplier) === 1.25
                      ? "border-4 border-blue-600 bg-green-200"
                      : "border-2 border-green-600 bg-green-200"
                  }`}>
                    <p className="text-[10px] sm:text-xs font-semibold text-green-900 mb-0.5 sm:mb-1">Excellent (80%)</p>
                    <p className="text-sm sm:text-base md:text-lg font-bold text-green-900">{(parseFloat(estimatedDistance.theoreticalKm) / 1.25).toFixed(2)} km</p>
                    <p className="text-[10px] sm:text-xs text-green-900">{(parseFloat(estimatedDistance.theoreticalMiles) / 1.25).toFixed(2)} mi</p>
                  </div>
                  <div className={`p-2 sm:p-3 md:p-4 rounded-lg ${
                    parseFloat(pingMultiplier) === 1
                      ? "border-4 border-blue-600 bg-purple-200"
                      : "border-2 border-purple-600 bg-purple-200"
                  }`}>
                    <p className="text-[10px] sm:text-xs font-semibold text-purple-900 mb-0.5 sm:mb-1">Theoretical (100%)</p>
                    <p className="text-sm sm:text-base md:text-lg font-bold text-purple-900">{estimatedDistance.theoreticalKm} km</p>
                    <p className="text-[10px] sm:text-xs text-purple-900">{estimatedDistance.theoreticalMiles} mi</p>
                  </div>
                </div>

                <div className="p-3 sm:p-4 md:p-6 border-2 border-gray-600 rounded-lg bg-gray-100">
                  <h3 className="font-semibold text-sm sm:text-base md:text-lg mb-2 sm:mb-3">Distance Range Summary</h3>
                  <div className="space-y-1.5 sm:space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs sm:text-sm font-semibold">Based on ping of {pingRTT}ms:</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-1.5 sm:py-2 border-t gap-1 sm:gap-0">
                      <span className="text-xs sm:text-sm">Minimum (Poor 33%)</span>
                      <span className="font-bold text-red-700 text-xs sm:text-sm">{(parseFloat(estimatedDistance.theoreticalKm) / 3).toFixed(2)} km ({(parseFloat(estimatedDistance.theoreticalMiles) / 3).toFixed(2)} mi)</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-1.5 sm:py-2 border-t gap-1 sm:gap-0">
                      <span className="text-xs sm:text-sm">Maximum (Theoretical 100%)</span>
                      <span className="font-bold text-purple-700 text-xs sm:text-sm">{estimatedDistance.theoreticalKm} km ({estimatedDistance.theoreticalMiles} mi)</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-1.5 sm:py-2 border-t bg-blue-50 px-2 rounded gap-1 sm:gap-0">
                      <span className="text-xs sm:text-sm font-semibold">Your Estimate ({pingMultiplier}x)</span>
                      <span className="font-bold text-blue-700 text-base sm:text-lg">{estimatedDistance.km} km ({estimatedDistance.miles} mi)</span>
                    </div>
                  </div>
                </div>

                <div className="p-3 sm:p-4 bg-muted rounded-lg">
                  <h3 className="font-semibold mb-1.5 sm:mb-2 text-xs sm:text-sm md:text-base">Calculation Details</h3>
                  <p className="text-xs sm:text-sm">
                    <span className="font-semibold">Medium:</span> {MEDIUM_TYPES[pingMediumType].name} - {MEDIUM_TYPES[pingMediumType].description}
                  </p>
                  <p className="text-xs sm:text-sm mt-1">
                    <span className="font-semibold">Speed:</span> {MEDIUM_TYPES[pingMediumType].speed.toLocaleString()} km/s
                  </p>
                  <p className="text-xs sm:text-sm mt-1">
                    <span className="font-semibold">Factor ({pingMultiplier}x):</span> Accounts for routing overhead, processing delays, and non-linear paths.
                  </p>
                  <p className="text-xs sm:text-sm mt-2 sm:mt-3 text-muted-foreground">
                    <span className="font-semibold">Note:</span> Estimate only. Actual distance may vary based on routing paths and network infrastructure.
                  </p>

                  {/* WonderNetwork Link for Ping Calculator */}
                  {pingCityWonderNetwork && (
                    <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 p-3 sm:p-4 rounded-lg">
                      <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                        <ExternalLink className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                        <p className="text-xs sm:text-sm md:text-base font-bold text-primary">Real-World Ping Stats</p>
                      </div>
                      <a
                        href={`https://wondernetwork.com/pings/${encodeURIComponent(pingCityWonderNetwork.displayName.toLowerCase())}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold text-primary hover:underline hover:text-primary/80 transition-colors ml-5 sm:ml-6 md:ml-7"
                      >
                        <ExternalLink className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                        <span className="break-words">View from {pingCityWonderNetwork.displayName}</span>
                      </a>
                    </div>
                  )}
                </div>

                <div className="p-3 sm:p-4 border rounded-lg">
                  <h3 className="font-semibold mb-2 sm:mb-3 text-xs sm:text-sm md:text-base">Distance Interpretation</h3>
                  <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                    <div className="flex justify-between items-center">
                      <span>0-500 km</span>
                      <span className="text-green-700 font-semibold">Local</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>500-2000 km</span>
                      <span className="text-yellow-700 font-semibold">National</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>2000-8000 km</span>
                      <span className="text-orange-700 font-semibold">Continental</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>8000+ km</span>
                      <span className="text-red-700 font-semibold">Intercontinental</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "transfer" && (
        <div className="w-full">
          <div className="p-3 sm:p-4 md:p-6">
            <h3 className="text-base sm:text-lg md:text-xl">Bandwidth / File Transfer Calculator</h3>
            <p className="text-xs sm:text-sm">Calculate how long it takes to transfer data based on file size, bandwidth, and network conditions.</p>
          </div>
          <div className="space-y-3 sm:space-y-4 p-3 sm:p-4 md:p-6">
            {/* File Size Presets */}
            <div className="space-y-1.5 sm:space-y-2">
              <label className="text-xs sm:text-sm font-medium">Quick File Size Presets</label>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {FILE_SIZE_PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    onClick={() => {
                      setFileSize(preset.value);
                      setFileSizeUnit(preset.unit);
                    }}
                    className={`px-2 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs rounded-md border transition-colors ${
                      fileSize === preset.value && fileSizeUnit === preset.unit
                        ? "bg-[#2813e8] text-white border-[#2813e8]"
                        : "bg-white hover:bg-gray-50 border-gray-300"
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5 sm:space-y-2">
                <label htmlFor="fileSize" className="text-xs sm:text-sm">File Size</label>
                <div className="flex gap-1.5 sm:gap-2 items-center">
                  <input
                    type="range"
                    min="1"
                    max="10000"
                    step="1"
                    value={fileSize || "1"}
                    onChange={(e) => setFileSize(e.target.value)}
                    aria-label="File size"
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#2813e8]"
                  />
                  <Input
                    id="fileSize"
                    type="number"
                    placeholder="Size"
                    value={fileSize}
                    onChange={(e) => setFileSize(e.target.value)}
                    className="w-20 text-xs sm:text-sm h-10"
                  />
                  <select aria-label="File size unit" value={fileSizeUnit} onChange={(event) => setFileSizeUnit(event.target.value)} className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-[#2813e8] focus:border-transparent h-10 text-xs sm:text-sm">
                      <option value="bytes">bytes</option>
                      <option value="KB">KB</option>
                      <option value="MB">MB</option>
                      <option value="GB">GB</option>
                      <option value="TB">TB</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1.5 sm:space-y-2">
                <label htmlFor="bandwidth" className="text-xs sm:text-sm">Connection Speed</label>
                <div className="flex gap-1.5 sm:gap-2 items-center">
                  <input
                    type="range"
                    min="1"
                    max="10000"
                    step="1"
                    value={bandwidth || "1"}
                    onChange={(e) => setBandwidth(e.target.value)}
                    aria-label="Connection speed"
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#2813e8]"
                  />
                  <Input
                    id="bandwidth"
                    type="number"
                    placeholder="Speed"
                    value={bandwidth}
                    onChange={(e) => setBandwidth(e.target.value)}
                    className="w-20 text-xs sm:text-sm h-10"
                  />
                  <select aria-label="Bandwidth unit" value={bandwidthUnit} onChange={(event) => setBandwidthUnit(event.target.value)} className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-[#2813e8] focus:border-transparent h-10 text-xs sm:text-sm">
                      <option value="Kbps">Kbps</option>
                      <option value="Mbps">Mbps</option>
                      <option value="Gbps">Gbps</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Bandwidth Presets */}
            <div className="space-y-1.5 sm:space-y-2">
              <label className="text-xs sm:text-sm font-medium">Quick Speed Presets</label>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {BANDWIDTH_PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    onClick={() => {
                      setBandwidth(preset.value);
                      setBandwidthUnit(preset.unit);
                    }}
                    className={`px-2 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs rounded-md border transition-colors ${
                      bandwidth === preset.value && bandwidthUnit === preset.unit
                        ? "bg-[#2813e8] text-white border-[#2813e8]"
                        : "bg-white hover:bg-gray-50 border-gray-300"
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Protocol Overhead */}
            <div className="space-y-1.5 sm:space-y-2">
              <label htmlFor="protocolOverhead" className="text-xs sm:text-sm">Protocol Overhead</label>
              <select
                id="protocolOverhead"
                value={protocolOverhead}
                onChange={(event) => setProtocolOverhead(event.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-[#2813e8] focus:border-transparent h-10 text-xs sm:text-sm"
              >
                {Object.entries(PROTOCOL_OVERHEADS).map(([key, value]) => (
                  <option key={key} value={key}>{value.name} - {value.description}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5 sm:space-y-2">
                <label htmlFor="transferLatency" className="text-xs sm:text-sm">Network Latency (ms, optional)</label>
                <div className="flex gap-1.5 sm:gap-2 items-center">
                  <input
                    type="range"
                    min="0"
                    max="1000"
                    step="1"
                    value={transferLatency || "0"}
                    onChange={(e) => setTransferLatency(e.target.value)}
                    aria-label="Network latency"
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#2813e8]"
                  />
                  <Input
                    id="transferLatency"
                    type="number"
                    placeholder="e.g., 50"
                    value={transferLatency}
                    onChange={(e) => setTransferLatency(e.target.value)}
                    className="w-20 text-xs sm:text-sm h-10"
                  />
                  <span className="text-xs sm:text-sm text-muted-foreground w-8">ms</span>
                </div>
              </div>

              <div className="space-y-1.5 sm:space-y-2">
                <label htmlFor="mtu" className="text-xs sm:text-sm">MTU (bytes, optional)</label>
                <div className="flex gap-1.5 sm:gap-2 items-center">
                  <input
                    type="range"
                    min="500"
                    max="9000"
                    step="1"
                    value={mtu || "1500"}
                    onChange={(e) => setMtu(e.target.value)}
                    aria-label="MTU"
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#2813e8]"
                  />
                  <Input
                    id="mtu"
                    type="number"
                    placeholder="1500"
                    value={mtu}
                    onChange={(e) => setMtu(e.target.value)}
                    className="w-20 text-xs sm:text-sm h-10"
                  />
                  <span className="text-xs sm:text-sm text-muted-foreground w-12">bytes</span>
                </div>
                <p className="text-[10px] text-muted-foreground">1500 = Ethernet, 1492 = PPPoE, 9000 = Jumbo</p>
              </div>
            </div>

            <div className="p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-[10px] sm:text-xs text-blue-900">
                <span className="font-semibold">Note:</span> This calculator uses decimal units (1 MB = 1,000,000 bytes) which is standard for network speeds.
                The public internet typically has a maximum MTU of 1500 bytes. Jumbo frames (9000 MTU) only work on private networks/LANs.
              </p>
            </div>

            <button
              onClick={calculateTransferTime}
              disabled={isTransferCalculating}
              className="w-full bg-[#2813e8] text-white hover:bg-[#2813e8]/90 transition-colors font-semibold rounded-md text-xs sm:text-sm h-10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isTransferCalculating ? "Calculating..." : "Calculate Transfer Time"}
            </button>
            {transferError && <p className="text-red-700 text-xs sm:text-sm">{transferError}</p>}

            {transferResult && (
              <div className="space-y-3 sm:space-y-4 mt-4 sm:mt-6">
                {/* Primary Result - HH:MM:SS */}
                <div className="p-4 sm:p-6 border-2 border-primary rounded-lg bg-primary/5 text-center">
                  <p className="text-xs sm:text-sm text-muted-foreground mb-2">Estimated Transfer Time</p>
                  <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary font-mono">{transferResult.formattedTime}</p>
                  <p className="text-sm sm:text-base text-muted-foreground mt-2">{transferResult.totalTime}</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 md:gap-4">
                  <div className="p-2 sm:p-3 md:p-4 border rounded-lg">
                    <p className="text-xs sm:text-sm text-muted-foreground">Raw Transfer Time</p>
                    <p className="text-lg sm:text-xl md:text-2xl font-bold">{transferResult.transferTime}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">Without network overhead</p>
                  </div>
                  <div className="p-2 sm:p-3 md:p-4 border rounded-lg">
                    <p className="text-xs sm:text-sm text-muted-foreground">Effective Throughput</p>
                    <p className="text-lg sm:text-xl md:text-2xl font-bold">{transferResult.throughput}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">Actual transfer rate</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 md:gap-4">
                  <div className="p-2 sm:p-3 md:p-4 border rounded-lg">
                    <p className="text-xs sm:text-sm text-muted-foreground">Protocol Overhead</p>
                    <p className="text-base sm:text-lg md:text-xl font-bold">{transferResult.calculationBreakdown.protocolOverheadPercent}%</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">{PROTOCOL_OVERHEADS[protocolOverhead].name}</p>
                  </div>
                  <div className="p-2 sm:p-3 md:p-4 border rounded-lg">
                    <p className="text-xs sm:text-sm text-muted-foreground">Latency Impact</p>
                    <p className="text-base sm:text-lg md:text-xl font-bold">{transferResult.latencyImpact}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">% of total time</p>
                  </div>
                </div>

                <div className="p-3 sm:p-4 bg-muted rounded-lg">
                  <h3 className="font-semibold mb-1.5 sm:mb-2 text-xs sm:text-sm md:text-base">Calculation Breakdown</h3>
                  <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                    <div className="flex justify-between">
                      <span>File Size:</span>
                      <span className="font-semibold">{fileSize} {fileSizeUnit} ({(transferResult.calculationBreakdown.fileSizeBits / 8).toLocaleString()} bytes)</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Connection Speed:</span>
                      <span className="font-semibold">{bandwidth} {bandwidthUnit}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Protocol Overhead:</span>
                      <span className="font-semibold">{PROTOCOL_OVERHEADS[protocolOverhead].name}</span>
                    </div>
                    {transferLatency && (
                      <div className="flex justify-between">
                        <span>Network Latency:</span>
                        <span className="font-semibold">{transferLatency} ms</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>MTU:</span>
                      <span className="font-semibold">{mtu} bytes</span>
                    </div>
                    <div className="flex justify-between border-t pt-2 mt-2">
                      <span>Effective Bandwidth:</span>
                      <span className="font-semibold">{(transferResult.calculationBreakdown.effectiveBandwidthBps / 1000000).toFixed(2)} Mbps</span>
                    </div>
                  </div>
                  <p className="text-[10px] sm:text-xs text-muted-foreground mt-2 sm:mt-3">
                    <span className="font-semibold">Formula:</span> Time = (File Size in bits) / (Effective Bandwidth in bps)
                    <br />
                    Total time includes TCP connection overhead (2 RTTs), packet header overhead (40 bytes/packet), and bandwidth-delay product limitations.
                  </p>
                </div>

                <div className="p-3 sm:p-4 border rounded-lg">
                  <h3 className="font-semibold mb-2 sm:mb-3 text-xs sm:text-sm md:text-base">Common File Size Reference</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-2 text-xs">
                    <div className="p-1.5 sm:p-2 bg-muted rounded">
                      <div className="font-semibold text-[10px] sm:text-xs">Photo (JPEG)</div>
                      <div className="text-muted-foreground text-[10px] sm:text-xs">2-5 MB</div>
                    </div>
                    <div className="p-1.5 sm:p-2 bg-muted rounded">
                      <div className="font-semibold text-[10px] sm:text-xs">Song (MP3)</div>
                      <div className="text-muted-foreground text-[10px] sm:text-xs">3-10 MB</div>
                    </div>
                    <div className="p-1.5 sm:p-2 bg-muted rounded">
                      <div className="font-semibold text-[10px] sm:text-xs">DVD</div>
                      <div className="text-muted-foreground text-[10px] sm:text-xs">4.7 GB</div>
                    </div>
                    <div className="p-1.5 sm:p-2 bg-muted rounded">
                      <div className="font-semibold text-[10px] sm:text-xs">Blu-ray</div>
                      <div className="text-muted-foreground text-[10px] sm:text-xs">25 GB</div>
                    </div>
                    <div className="p-1.5 sm:p-2 bg-muted rounded">
                      <div className="font-semibold text-[10px] sm:text-xs">HD Movie</div>
                      <div className="text-muted-foreground text-[10px] sm:text-xs">4-8 GB</div>
                    </div>
                    <div className="p-1.5 sm:p-2 bg-muted rounded">
                      <div className="font-semibold text-[10px] sm:text-xs">4K Movie</div>
                      <div className="text-muted-foreground text-[10px] sm:text-xs">25-100 GB</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
